/**
 * Canvas renderer, ported from the prototype's draw(). Reads the Game and
 * ViewState; never mutates sim state. All magic numbers are the prototype's.
 */
import {
  CELL,
  FLY_ROUTE,
  GEMS,
  GREATER_SLATES,
  NX,
  NY,
  SLATES,
  SPECIALS,
  WAYPOINTS,
  WAYPOINT_SET,
} from '../data/balance.js';
import type { Game } from '../core/game.js';
import { waveDef } from '../core/waves.js';
import { drawGemIcon, shade } from './shapes.js';
import type { ViewState } from './viewState.js';

function inBounds(x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < NX && y < NY;
}

/** The sim's logical pixel space; all draw calls use these coordinates. */
const LOGICAL_W = NX * CELL;
const LOGICAL_H = NY * CELL;

export class Renderer {
  private readonly ctx: CanvasRenderingContext2D;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas context unavailable');
    this.ctx = ctx;
  }

  /**
   * Match the backing store to the displayed size x devicePixelRatio and map
   * the context back to logical 1120x640 space. Keeps the board crisp at any
   * window size (a fixed 1120px buffer blurs when CSS upscales it).
   */
  private syncSize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const w = Math.max(1, Math.round(rect.width * dpr));
    const h = Math.max(1, Math.round(rect.height * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    this.ctx.setTransform(w / LOGICAL_W, 0, 0, h / LOGICAL_H, 0, 0);
  }

  draw(game: Game, view: ViewState): void {
    const { ctx } = this;
    const { animT } = view;
    this.syncSize();
    ctx.clearRect(0, 0, LOGICAL_W, LOGICAL_H);

    // gridless board: a soft local grid follows the cursor
    if (view.hoverCell) {
      const hover = view.hoverCell;
      const R = 3;
      for (let dy = -R; dy <= R; dy++) {
        for (let dx = -R; dx <= R; dx++) {
          const x = hover.x + dx;
          const y = hover.y + dy;
          if (!inBounds(x, y)) continue;
          const d = Math.max(Math.abs(dx), Math.abs(dy));
          if (d === 0) continue;
          ctx.strokeStyle = `rgba(126,203,255,${(0.18 * (1 - d / (R + 1))).toFixed(3)})`;
          ctx.lineWidth = 1;
          ctx.strokeRect(x * CELL + 0.5, y * CELL + 0.5, CELL - 1, CELL - 1);
        }
      }
      const hx = hover.x * CELL;
      const hy = hover.y * CELL;
      const cellObj = game.grid[hover.y]![hover.x] ?? null;
      const canPickup =
        game.phase !== 'wave' &&
        cellObj !== null &&
        cellObj.kind === 'gem' &&
        cellObj.isNew &&
        cellObj.special === undefined;
      const blockedHere = cellObj !== null || WAYPOINT_SET.has(`${hover.x},${hover.y}`);
      const col = canPickup
        ? '255,210,87'
        : game.phase === 'build' && blockedHere
          ? '255,107,122'
          : '126,203,255';
      ctx.save();
      ctx.fillStyle = `rgba(${col},0.10)`;
      ctx.fillRect(hx, hy, CELL, CELL);
      ctx.shadowColor = `rgb(${col})`;
      ctx.shadowBlur = 10;
      ctx.strokeStyle = `rgba(${col},0.9)`;
      ctx.lineWidth = 1.5;
      ctx.strokeRect(hx + 0.75, hy + 0.75, CELL - 1.5, CELL - 1.5);
      ctx.restore();
    }

    // held lifted piece: ghost it at the target cell (touch pending spot or
    // hover cell) so the player sees what they are about to drop where
    const ghostCell = view.pendingPlace ?? view.hoverCell;
    if (game.lifted && ghostCell && !game.grid[ghostCell.y]?.[ghostCell.x]) {
      const piece = game.lifted;
      const gx = ghostCell.x * CELL;
      const gy = ghostCell.y * CELL;
      ctx.save();
      ctx.globalAlpha = 0.55;
      if (piece.kind !== 'gem') {
        // Rocks only: slates are anchored and can never be lifted.
        ctx.fillStyle = '#3d4250';
        ctx.fillRect(gx + 0.5, gy + 0.5, CELL - 1, CELL - 1);
        ctx.fillStyle = '#484e5e';
        ctx.fillRect(gx + 2.5, gy + 2.5, CELL - 8, CELL - 8);
      } else if (piece.special !== undefined) {
        drawGemIcon(
          ctx,
          'star',
          SPECIALS[piece.special].color,
          gx + CELL / 2,
          gy + CELL / 2,
          9,
          false,
        );
      } else {
        drawGemIcon(
          ctx,
          GEMS[piece.type].shape,
          GEMS[piece.type].color,
          gx + CELL / 2,
          gy + CELL / 2,
          5.5 + piece.tier * 0.8,
          false,
        );
      }
      ctx.globalAlpha = 1;
      ctx.strokeStyle = '#ffd257';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 3]);
      ctx.strokeRect(gx + 1, gy + 1, CELL - 2, CELL - 2);
      ctx.setLineDash([]);
      ctx.restore();
    }

    // touch flow: the ghost stone awaiting its confirming tap
    if (view.pendingPlace) {
      const px = view.pendingPlace.x * CELL;
      const py = view.pendingPlace.y * CELL;
      const pulse = 0.5 + 0.5 * Math.sin(animT * 6);
      ctx.save();
      ctx.fillStyle = 'rgba(255,210,87,0.18)';
      ctx.fillRect(px + 1, py + 1, CELL - 2, CELL - 2);
      ctx.shadowColor = '#ffd257';
      ctx.shadowBlur = 6 + 6 * pulse;
      ctx.strokeStyle = '#ffd257';
      ctx.lineWidth = 2;
      ctx.strokeRect(px + 1, py + 1, CELL - 2, CELL - 2);
      ctx.restore();
    }

    // ground path: soft glow underlay + bright core + direction chevrons
    if (game.fullPath) {
      const path = game.fullPath;
      const dim = game.phase === 'wave';
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.beginPath();
      path.forEach((p, i) => {
        const px = (p.x + 0.5) * CELL;
        const py = (p.y + 0.5) * CELL;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      });
      ctx.strokeStyle = dim ? 'rgba(126,203,255,0.04)' : 'rgba(126,203,255,0.07)';
      ctx.lineWidth = 11;
      ctx.stroke();
      ctx.strokeStyle = dim ? 'rgba(126,203,255,0.10)' : 'rgba(126,203,255,0.22)';
      ctx.lineWidth = 3.5;
      ctx.stroke();
      if (!dim) {
        ctx.fillStyle = '#7ecbff';
        for (let i = 4; i < path.length - 1; i += 7) {
          const a = path[i]!;
          const b = path[i + 1]!;
          const ang = Math.atan2(b.y - a.y, b.x - a.x);
          ctx.save();
          ctx.translate((a.x + 0.5) * CELL, (a.y + 0.5) * CELL);
          ctx.rotate(ang);
          ctx.globalAlpha = 0.22 + 0.2 * Math.sin(animT * 3 + i);
          ctx.beginPath();
          ctx.moveTo(4.5, 0);
          ctx.lineTo(-2.5, -4);
          ctx.lineTo(-2.5, 4);
          ctx.closePath();
          ctx.fill();
          ctx.restore();
        }
        ctx.globalAlpha = 1;
      }
    }

    // flying route: animated dashes over the walls when an air wave is near
    const def = waveDef(game.wave);
    if (def.flying && game.phase !== 'gameover') {
      ctx.strokeStyle = 'rgba(215,140,255,0.4)';
      ctx.lineWidth = 2;
      ctx.setLineDash([7, 7]);
      ctx.lineDashOffset = -animT * 20;
      ctx.beginPath();
      FLY_ROUTE.forEach((p, i) => {
        const px = (p.x + 0.5) * CELL;
        const py = (p.y + 0.5) * CELL;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      });
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // waypoints: portal discs with pulsing rings
    ctx.font = 'bold 11px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (let wi = 0; wi < WAYPOINTS.length; wi++) {
      const w = WAYPOINTS[wi]!;
      const wcx = (w.x + 0.5) * CELL;
      const wcy = (w.y + 0.5) * CELL;
      const col = w.label === 'IN' ? '#5fd068' : w.label === 'OUT' ? '#ff6b7a' : '#7ecbff';
      const pulse = 0.5 + 0.5 * Math.sin(animT * 2.5 + wi * 1.3);
      ctx.globalAlpha = 0.2 + 0.3 * pulse;
      ctx.strokeStyle = col;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(wcx, wcy, 13 + pulse * 3, 0, 7);
      ctx.stroke();
      ctx.globalAlpha = 1;
      const wgrad = ctx.createRadialGradient(wcx, wcy - 3, 1, wcx, wcy, 12);
      wgrad.addColorStop(0, shade(col, 10));
      wgrad.addColorStop(1, shade(col, -75));
      ctx.beginPath();
      ctx.arc(wcx, wcy, 11, 0, 7);
      ctx.fillStyle = wgrad;
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.55)';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.lineJoin = 'round';
      // Longer labels (OUT) shrink to stay inside the 11px-radius disc.
      ctx.font = w.label.length >= 3 ? 'bold 8px sans-serif' : 'bold 11px sans-serif';
      ctx.strokeStyle = 'rgba(0,0,0,0.8)';
      ctx.lineWidth = 2.5;
      ctx.strokeText(w.label, wcx, wcy + 0.5);
      ctx.fillStyle = '#fff';
      ctx.fillText(w.label, wcx, wcy + 0.5);
      ctx.font = 'bold 11px sans-serif';
    }

    // slate fields (drawn beneath gems, like auras): a soft disc + dashed ring
    for (let y = 0; y < NY; y++) {
      for (let x = 0; x < NX; x++) {
        const c = game.grid[y]![x];
        if (!c || c.kind !== 'slate') continue;
        const def = SLATES[c.key];
        const scx = (x + 0.5) * CELL;
        const scy = (y + 0.5) * CELL;
        const rgb = Renderer.hexToRgb(def.color);
        const pulse = 0.5 + 0.5 * Math.sin(animT * 1.6 + x * 2.1);
        ctx.beginPath();
        ctx.arc(scx, scy, def.radius * CELL, 0, 7);
        ctx.fillStyle = `rgba(${rgb},0.045)`;
        ctx.fill();
        ctx.setLineDash([3, 11]);
        ctx.lineDashOffset = animT * 10;
        ctx.strokeStyle = `rgba(${rgb},${(0.14 + 0.14 * pulse).toFixed(3)})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }

    // aura fields (drawn beneath gems)
    for (let y = 0; y < NY; y++) {
      for (let x = 0; x < NX; x++) {
        const g = game.grid[y]![x];
        if (!g || g.kind !== 'gem' || g.isNew) continue;
        let acolor: string | null = null;
        let arange = 0;
        if (g.special === 'blackopal') {
          acolor = '150,130,220';
          arange = SPECIALS.blackopal.range;
        } else if (g.special === undefined && g.type === 'opal') {
          acolor = '139,232,216';
          arange = GEMS.opal.range + g.tier * 0.2;
        }
        if (!acolor) continue;
        const acx = (x + 0.5) * CELL;
        const acy = (y + 0.5) * CELL;
        const pulse = 0.5 + 0.5 * Math.sin(animT * 2 + x * 1.7);
        ctx.beginPath();
        ctx.arc(acx, acy, arange * CELL, 0, 7);
        ctx.fillStyle = `rgba(${acolor},0.055)`;
        ctx.fill();
        ctx.setLineDash([6, 9]);
        ctx.lineDashOffset = -animT * 15;
        ctx.strokeStyle = `rgba(${acolor},${(0.16 + 0.16 * pulse).toFixed(3)})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }

    // cells
    for (let y = 0; y < NY; y++) {
      for (let x = 0; x < NX; x++) {
        const c = game.grid[y]![x];
        if (!c) continue;
        const px = x * CELL;
        const py = y * CELL;
        if (c.kind === 'rock') {
          ctx.fillStyle = '#3d4250';
          ctx.fillRect(px + 0.5, py + 0.5, CELL - 1, CELL - 1);
          ctx.fillStyle = '#484e5e';
          ctx.fillRect(px + 2.5, py + 2.5, CELL - 8, CELL - 8);
          ctx.strokeStyle = '#2a2e3a';
          ctx.lineWidth = 1;
          ctx.strokeRect(px + 0.5, py + 0.5, CELL - 1, CELL - 1);
        } else if (c.kind === 'slate') {
          // A rock that glows: dark base, slate-colored inset, diamond sigil.
          const def = SLATES[c.key];
          ctx.fillStyle = '#3d4250';
          ctx.fillRect(px + 0.5, py + 0.5, CELL - 1, CELL - 1);
          ctx.save();
          ctx.shadowColor = def.color;
          ctx.shadowBlur = 5 + 3 * Math.sin(animT * 2 + x);
          ctx.fillStyle = shade(def.color, -55);
          ctx.fillRect(px + 2.5, py + 2.5, CELL - 5, CELL - 5);
          ctx.restore();
          drawGemIcon(ctx, 'diamond', def.color, px + CELL / 2, py + CELL / 2, 6.5, true);
          ctx.strokeStyle = shade(def.color, -30);
          ctx.lineWidth = 1;
          ctx.strokeRect(px + 0.5, py + 0.5, CELL - 1, CELL - 1);
        } else {
          const cx = px + CELL / 2;
          const cy = py + CELL / 2;
          if (c.special !== undefined) {
            drawGemIcon(ctx, 'star', SPECIALS[c.special].color, cx, cy, 9, true);
          } else {
            drawGemIcon(
              ctx,
              GEMS[c.type].shape,
              GEMS[c.type].color,
              cx,
              cy,
              5.5 + c.tier * 0.8,
              c.tier >= 3,
            );
          }
          if (c.isNew) {
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 1.5;
            ctx.strokeRect(px + 1.5, py + 1.5, CELL - 3, CELL - 3);
            const idx = game.newGems.indexOf(c);
            if (idx >= 0) {
              ctx.font = 'bold 11px sans-serif';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.lineJoin = 'round';
              ctx.strokeStyle = 'rgba(0,0,0,0.85)';
              ctx.lineWidth = 3;
              ctx.strokeText(String(idx + 1), px + CELL / 2, py + CELL / 2);
              ctx.fillStyle = '#fff';
              ctx.fillText(String(idx + 1), px + CELL / 2, py + CELL / 2);
            }
          }
        }
      }
    }

    // range ring
    const focus = view.selectedCell ?? view.hoverCell;
    if (focus) {
      const g = game.grid[focus.y]?.[focus.x];
      if (g && g.kind === 'gem') {
        const st = game.gemStats(g);
        ctx.strokeStyle = 'rgba(255,255,255,0.35)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc((focus.x + 0.5) * CELL, (focus.y + 0.5) * CELL, st.range * CELL, 0, 7);
        ctx.stroke();
      } else if (g && g.kind === 'slate') {
        ctx.strokeStyle = SLATES[g.key].color;
        ctx.globalAlpha = 0.5;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc((focus.x + 0.5) * CELL, (focus.y + 0.5) * CELL, SLATES[g.key].radius * CELL, 0, 7);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
    }

    // slate socket picker: highlight every rock that can take the pending slate
    if (view.pendingSlate) {
      const key = view.pendingSlate.key;
      const pulse = 0.5 + 0.5 * Math.sin(animT * 5);
      const col = SLATES[key].color;
      for (let y = 0; y < NY; y++) {
        for (let x = 0; x < NX; x++) {
          if (!game.canSocketAt(key, x, y)) continue;
          ctx.save();
          ctx.shadowColor = col;
          ctx.shadowBlur = 4 + 5 * pulse;
          ctx.strokeStyle = col;
          ctx.globalAlpha = 0.5 + 0.4 * pulse;
          ctx.lineWidth = 1.5;
          ctx.strokeRect(x * CELL + 1.5, y * CELL + 1.5, CELL - 3, CELL - 3);
          ctx.restore();
        }
      }
      // ring preview on the hovered eligible rock
      if (view.hoverCell && game.canSocketAt(key, view.hoverCell.x, view.hoverCell.y)) {
        ctx.strokeStyle = col;
        ctx.globalAlpha = 0.6;
        ctx.setLineDash([4, 6]);
        ctx.beginPath();
        ctx.arc(
          (view.hoverCell.x + 0.5) * CELL,
          (view.hoverCell.y + 0.5) * CELL,
          SLATES[key].radius * CELL,
          0,
          7,
        );
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;
      }
    }

    // greater-slate combine: mark the selection, pulse its valid partners
    if (view.pendingSlateUp) {
      const sel = view.pendingSlateUp;
      const selCell = game.grid[sel.y]?.[sel.x];
      if (selCell?.kind === 'slate') {
        ctx.strokeStyle = '#ffd257';
        ctx.lineWidth = 2;
        ctx.strokeRect(sel.x * CELL + 1, sel.y * CELL + 1, CELL - 2, CELL - 2);
        const pulse = 0.5 + 0.5 * Math.sin(animT * 5);
        for (let y = 0; y < NY; y++) {
          for (let x = 0; x < NX; x++) {
            const c = game.grid[y]![x];
            if (!c || c.kind !== 'slate' || (x === sel.x && y === sel.y)) continue;
            const greater = GREATER_SLATES.find(
              (gs) =>
                (gs.pair[0] === selCell.key && gs.pair[1] === c.key) ||
                (gs.pair[0] === c.key && gs.pair[1] === selCell.key),
            );
            if (!greater) continue;
            ctx.save();
            ctx.shadowColor = SLATES[greater.key].color;
            ctx.shadowBlur = 4 + 6 * pulse;
            ctx.strokeStyle = SLATES[greater.key].color;
            ctx.globalAlpha = 0.55 + 0.4 * pulse;
            ctx.lineWidth = 2;
            ctx.strokeRect(x * CELL + 1, y * CELL + 1, CELL - 2, CELL - 2);
            ctx.restore();
          }
        }
      }
    }

    // action preview: gold = result, red X = consumed, dimmed = becomes rock
    if (view.actionPreview && game.phase === 'choose') {
      const preview = view.actionPreview;
      const pp = 0.6 + 0.4 * Math.sin(animT * 5);
      for (const d of preview.dim) {
        ctx.fillStyle = 'rgba(10,12,18,0.55)';
        ctx.fillRect(d.x * CELL + 1, d.y * CELL + 1, CELL - 2, CELL - 2);
      }
      for (const c of preview.consume) {
        const px = c.x * CELL;
        const py = c.y * CELL;
        ctx.strokeStyle = `rgba(255,107,122,${(0.5 + 0.5 * pp).toFixed(2)})`;
        ctx.lineWidth = 2;
        ctx.strokeRect(px + 1.5, py + 1.5, CELL - 3, CELL - 3);
        ctx.beginPath();
        ctx.moveTo(px + 5, py + 5);
        ctx.lineTo(px + CELL - 5, py + CELL - 5);
        ctx.moveTo(px + CELL - 5, py + 5);
        ctx.lineTo(px + 5, py + CELL - 5);
        ctx.stroke();
      }
      if (preview.result) {
        const px = preview.result.x * CELL;
        const py = preview.result.y * CELL;
        ctx.save();
        ctx.shadowColor = '#ffd257';
        ctx.shadowBlur = 6 + 8 * pp;
        ctx.strokeStyle = '#ffd257';
        ctx.lineWidth = 2;
        ctx.strokeRect(px + 1, py + 1, CELL - 2, CELL - 2);
        ctx.restore();
      }
    }

    // creeps
    for (const c of game.creeps) {
      if (c.dead) continue;
      const r = c.mega ? 13 : c.boss ? 9 : 6;
      if (c.flying) {
        ctx.fillStyle = 'rgba(0,0,0,0.35)';
        ctx.beginPath();
        ctx.ellipse(c.px + 4, c.py + 7, r * 0.8, r * 0.4, 0, 0, 7);
        ctx.fill();
        ctx.fillStyle = c.boss ? '#ff9e3d' : '#d78cff';
        ctx.beginPath();
        ctx.moveTo(c.px, c.py - r);
        ctx.lineTo(c.px + r, c.py + r);
        ctx.lineTo(c.px - r, c.py + r);
        ctx.closePath();
        ctx.fill();
      } else {
        ctx.fillStyle = c.mega ? '#ff5470' : c.boss ? '#ff9e3d' : '#c8524f';
        ctx.beginPath();
        ctx.arc(c.px, c.py, r, 0, 7);
        ctx.fill();
        if (c.mega) {
          ctx.strokeStyle = '#ffd257';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }
      if (game.gt < c.poisonUntil && c.poisonDps) {
        ctx.strokeStyle = '#2ecc71';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(c.px, c.py, r + 2, 0, 7);
        ctx.stroke();
      }
      ctx.fillStyle = '#000';
      ctx.fillRect(c.px - 9, c.py - r - 7, 18, 3);
      ctx.fillStyle = '#5fd068';
      ctx.fillRect(c.px - 9, c.py - r - 7, 18 * Math.max(0, c.hp / c.maxhp), 3);
    }

    for (const p of game.projectiles) {
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 2.5, 0, 7);
      ctx.fill();
    }

    ctx.font = 'bold 11px sans-serif';
    ctx.textAlign = 'center';
    for (const f of view.floats) {
      ctx.globalAlpha = Math.min(1, f.t);
      ctx.fillStyle = f.color;
      ctx.fillText(f.txt, f.x, f.y - (1 - f.t) * 18);
      ctx.globalAlpha = 1;
    }

    // End-of-run overlay. The old build-51 victory banner is gone: under
    // endless rules (ADR 0007) round 51 is live play, not an end state.
    if (game.phase === 'gameover') {
      const result = game.recording.result;
      // Dim the whole board hard, then deepen the text band on top.
      ctx.fillStyle = 'rgba(0,0,0,0.62)';
      ctx.fillRect(0, 0, LOGICAL_W, LOGICAL_H);
      ctx.fillStyle = 'rgba(0,0,0,0.45)';
      ctx.fillRect(0, LOGICAL_H / 2 - 70, LOGICAL_W, 130);
      ctx.fillStyle = game.won ? '#ffd257' : '#ff6b7a';
      ctx.font = 'bold 38px sans-serif';
      ctx.fillText(game.won ? 'VICTORY!' : 'GAME OVER', LOGICAL_W / 2, LOGICAL_H / 2 - 15);
      ctx.font = '16px sans-serif';
      ctx.fillStyle = '#d8dce6';
      ctx.fillText(
        game.won
          ? `Wave 50 conquered · reached wave ${result?.wave ?? game.wave} in the endless waves`
          : `Reached wave ${game.wave} · Restart to try again`,
        LOGICAL_W / 2,
        LOGICAL_H / 2 + 25,
      );
    }
  }

  /** '#8fa3c8' -> '143,163,200' for rgba() composition. */
  private static hexToRgb(hex: string): string {
    return `${parseInt(hex.slice(1, 3), 16)},${parseInt(hex.slice(3, 5), 16)},${parseInt(hex.slice(5, 7), 16)}`;
  }
}
